import styled, { css } from 'styled-components';

// Media query breakpoints
const breakpoints = {
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px'
};

// Media query helper
const media = Object.keys(breakpoints).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${breakpoints[label]}) {
      ${css(...args)}
    }
  `;
  return acc;
}, {});

// Base Card component
export const Card = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.small};
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  font-family: ${props => props.theme.typography.fontFamily};

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props => props.theme.shadows.medium};
  }

  ${media.md`
    padding: ${props => props.theme.spacing.xl};
  `}
`;

// Login specific card
export const LoginCard = styled(Card)`
  width: 100%;
  max-width: 500px; 
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: ${({ theme }) => theme.shadows.large}; 
  border: none; 
  margin-bottom: 0; 
  z-index: 1;
  
  &:hover {
    transform: none; // Remove hover effect for login card
    box-shadow: ${({ theme }) => theme.shadows.large}; // Keep original shadow
  }
`;

// Action card with click functionality
export const ActionCard = styled(Card)`
  cursor: pointer;
  text-align: center;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, background-color 0.2s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.large};
    background-color: ${props => props.theme.colors.hoverBackground || props.theme.colors.background};
  }

  &:active {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
`;

// Dashboard card variant
export const DashboardCard = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 200px;
  
  ${media.md`
    min-height: 250px;
  `}
`;

// Compact card for smaller content
export const CompactCard = styled(Card)`
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
  
  ${media.md`
    padding: ${props => props.theme.spacing.lg};
  `}

  &:hover {
    transform: translateY(-2px);
  }
`;

// Feature card with icon support
export const FeatureCard = styled(Card)`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  
  ${media.md`
    padding: ${props => props.theme.spacing.xxl};
  `}
`;

// Card with colored border
export const BorderCard = styled(Card)`
  border-left: 4px solid ${props => props.borderColor || props.theme.colors.primary};
  
  &:hover {
    border-left-color: ${props => props.hoverBorderColor || props.theme.colors.secondary};
  }
`;

// Card header component
export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

// Card body component
export const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// Card footer component
export const CardFooter = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// Card title component
export const CardTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSizes.lg};
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-top: 0;
  margin-bottom: ${props => props.theme.spacing.md};
  font-family: ${props => props.theme.typography.fontFamily};
  border-bottom: ${props => props.borderWidth || '2px'} solid ${props => props.borderColor || `${props.theme.colors.primary}20`};
  padding-bottom: ${props => props.theme.spacing.sm};
  
  
  ${media.md`
    font-size: ${props => props.theme.typography.fontSizes.xl};
  `}
`;

// Card subtitle component
export const CardSubtitle = styled.h4`
  font-size: ${props => props.theme.typography.fontSizes.md};
  font-weight: ${props => props.theme.typography.fontWeights.regular};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.sm};
  font-family: ${props => props.theme.typography.fontFamily};
`;

// Card content component
export const CardContent = styled.div`
  font-size: ${props => props.theme.typography.fontSizes.md};
  line-height: 1.6;
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily};
  align-items: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  // min-height: 120px;
`;

// Card image component
export const CardImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: ${props => props.theme.borderRadius.medium};
  margin-bottom: ${props => props.theme.spacing.md};
  object-fit: cover;
`;

// Card icon wrapper
export const CardIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background-color: ${props => props.backgroundColor || props.theme.colors.primaryLight};
  border-radius: ${props => props.theme.borderRadius.round};
  margin: 0 auto ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.typography.fontSizes.xl};
  color: ${props => props.color || props.theme.colors.primary};
  
  ${media.md`
    width: 80px;
    height: 80px;
    font-size: ${props => props.theme.typography.fontSizes.xxl};
  `}
`;

// Notification card variant
export const NotificationCard = styled(Card)`
  border-left: 4px solid ${props => {
    switch (props.type) {
      case 'success': return props.theme.colors.success;
      case 'warning': return props.theme.colors.warning;
      case 'error': return props.theme.colors.error;
      case 'info': return props.theme.colors.info || props.theme.colors.primary;
      default: return props.theme.colors.primary;
    }
  }};
  
  background-color: ${props => {
    switch (props.type) {
      case 'success': return props.theme.colors.successLight || `${props.theme.colors.success}15`;
      case 'warning': return props.theme.colors.warningLight || `${props.theme.colors.warning}15`;
      case 'error': return props.theme.colors.errorLight || `${props.theme.colors.error}15`;
      case 'info': return props.theme.colors.infoLight || `${props.theme.colors.primary}15`;
      default: return props.theme.colors.background;
    }
  }};
`;

// Statistics card
export const StatsCard = styled(Card)`
  text-align: center;
  padding: ${props => props.theme.spacing.lg};
  
  ${media.md`
    padding: ${props => props.theme.spacing.xl};
  `}
`;

// Logo components for cards
export const CardLogo = styled.img`
  height: ${props => props.size === 'large' ? '120px' : props.size === 'small' ? '60px' : '80px'};
  width: auto;
  margin: 0 auto ${props => props.theme.spacing.lg};
  display: block;
  object-fit: contain;
`;

// Container for multiple cards
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.lg};
  
  ${media.sm`
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  `}
  
  ${media.lg`
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: ${props => props.theme.spacing.xl};
  `}
`;

// Card actions wrapper
export const CardActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.lg};
  justify-content: ${props => props.justify || 'flex-start'};
  flex-wrap: wrap;
  
  ${media.sm`
    flex-wrap: nowrap;
    gap: ${props => props.theme.spacing.md};
  `}
`;